import { create } from 'zustand';
import type { 
  ServiceLayer, 
  GisFeature, 
  GisFeatureCollection, 
  PdsShop, 
  PdsProperties,
  TnebSection, 
  Geometry,
  Point,
  ConstituencyProperties,
  PoliceBoundaryProperties,
  PoliceStationProperties,
  PoliceResolutionResult,
  PostalOffice,
  HealthManifest,
  HealthFacilityProperties,
  HealthFacility,
  HealthScope,
  HealthFilters,
  HealthSummary,
  ElectionResult,
  VeterinaryFacility,
  VeterinaryFacilityProperties,
  LandParcel,
  TempleProperties,
  RadioStation,
  RadioStationProperties,
  FuelStation,
  FuelStationProperties,
  FuelManifest,
  FuelFilters,
  FuelScope,
  FuelSummary
} from '../types/gis';
import type { LocalBodyV2Feature } from '../types/gis_v2';
import { trackEvent } from '../lib/firebase';

export interface StateSummary {
  party: string;
  won: number;
  leading: number;
  total: number;
}

interface MapState {
  view: {
    center: [number, number];
    zoom: number;
  };
  activeLayer: ServiceLayer;
  searchQuery: string;
  searchSuggestions: GisFeature[];
  selectedSuggestion: GisFeature | null;
  focusedSuggestionIndex: number; // For keyboard navigation
  searchResult: GisFeature | null; // This is the Pincode highlight
  districtsData: GisFeatureCollection | null; // All district boundaries
  stateBoundaryData: GisFeatureCollection | null; // State boundary
  pdsData: GisFeatureCollection<Geometry, PdsProperties> | null;
  selectedPdsShop: PdsShop | null;
  activeDistrict: string | null;
  jurisdictionDetails: TnebSection | null; // This is the TNEB office data
  jurisdictionGeometry: Geometry | null; // This is the TNEB polygon
  isResolving: boolean;
  isLocating: boolean;
  isSearching: boolean;
  isVeterinaryLoading: boolean;
  theme: 'dark' | 'light';
  isSidebarOpen: boolean;
  triggerLocateMe: boolean;
  isReportModalOpen: boolean;
  reportContext: { type: string; data: { label: string; value: string }[] } | null;
  noDataFound: boolean;
  lastClickedPoint: { lat: number; lng: number } | null;
  constituencyType: 'AC' | 'PC';
  acData: GisFeatureCollection<Geometry, ConstituencyProperties> | null;
  pcData: GisFeatureCollection<Geometry, ConstituencyProperties> | null;
  policeBoundariesData: GisFeatureCollection<Geometry, PoliceBoundaryProperties> | null;
  policeStationsData: GisFeatureCollection<Point, PoliceStationProperties> | null;
  selectedPoliceStation: GisFeature<Point, PoliceStationProperties> | null;
  policeResolution: PoliceResolutionResult | null;
  selectedPostalOffices: PostalOffice[] | null;
  selectedPostalOffice: PostalOffice | null;
  healthManifest: HealthManifest | null;
  healthPriorityData: GisFeatureCollection<Point, HealthFacilityProperties> | null;
  healthDistrictData: GisFeatureCollection<Point, HealthFacilityProperties> | null;
  selectedHealthFacility: HealthFacility | null;
  healthScope: HealthScope;
  healthFilters: HealthFilters;
  healthSummary: HealthSummary | null;
  isHealthLoading: boolean;
  targetHealthScope: HealthScope | null;
  veterinaryData: GisFeatureCollection<Point, VeterinaryFacilityProperties> | null;
  selectedVeterinaryFacility: VeterinaryFacility | null;
  veterinaryFilters: string[];
  registrationData: GisFeatureCollection | null;
  selectedRegistrationOffice: GisFeature | null;
  isLegalModalOpen: boolean;
  legalTab: 'about' | 'disclaimer' | 'legal';
  postalFilters: {
    delivery: 'All' | 'Delivery' | 'Non Delivery';
    type: 'All' | 'HO' | 'SO' | 'BO';
  };
  selectedLocalBodyV2: LocalBodyV2Feature | null;
  isV2Loading: boolean;
  language: 'en' | 'ta';
  globalLocation: {
    lat: number | null;
    lng: number | null;
    source: 'manual' | 'link' | 'pincode' | 'gps' | null;
    rawInput: string;
  } | null;
  userLocation: { lat: number; lng: number } | null;
  hasSeenTutorial: boolean;
  isTutorialOpen: boolean;
  hasNetworkError: boolean;
  isResultMinimized: boolean;
  election: {
    results: Record<number, ElectionResult> | null;
    lastUpdated: string | null;
    isLive: boolean;
    stateSummary: StateSummary[] | null;
    isLoading: boolean;
  };
  pendingPincodeResolve: string | null;
  pendingVeterinaryResolve: string | null;
  pendingRegistrationResolve: string | null;
  pendingLandParcelResolve: string | null;
  autoLocateAttempted: boolean;
  locateTriggerSource: 'auto' | 'manual' | null;
  selectedLandParcel: LandParcel | null;
  isLandParcelLoading: boolean;
  landParcelError: 'timeout' | 'error' | null;
  selectedTemples: TempleProperties[] | null;
  selectedTemple: TempleProperties | null;
  pendingTempleResolve: string | null;
  radioData: GisFeatureCollection<Point, RadioStationProperties> | null;
  selectedRadioStation: RadioStation | null;
  isRadioPlaying: boolean;
  fuelManifest: FuelManifest | null;
  fuelDistrictData: GisFeatureCollection<Point, FuelStationProperties> | null;
  selectedFuelStation: FuelStation | null;
  fuelScope: FuelScope;
  fuelFilters: FuelFilters;
  fuelSummary: FuelSummary | null;
  isFuelLoading: boolean;

  // Actions
  setHasSeenTutorial: (val: boolean) => void;
  setIsTutorialOpen: (val: boolean) => void;
  setSelectedLocalBodyV2: (feature: LocalBodyV2Feature | null) => void;
  setIsV2Loading: (val: boolean) => void;
  setLanguage: (lang: 'en' | 'ta') => void;
  setGlobalLocation: (location: { lat: number | null; lng: number | null; source: 'manual' | 'link' | 'pincode' | 'gps' | null; rawInput: string; } | null) => void;
  setUserLocation: (location: { lat: number; lng: number } | null) => void;
  setView: (center: [number, number], zoom: number) => void;
  setActiveLayer: (layer: ServiceLayer) => void;
  setSearchQuery: (query: string) => void;
  setSearchSuggestions: (suggestions: GisFeature[]) => void;
  setSelectedSuggestion: (suggestion: GisFeature | null) => void;
  setFocusedSuggestionIndex: (index: number) => void;
  setSearchResult: (result: GisFeature | null, keepSelection?: boolean, updateQuery?: boolean) => void;
  setDistrictsData: (data: GisFeatureCollection | null) => void;
  setStateBoundaryData: (data: GisFeatureCollection | null) => void;
  setPdsData: (data: GisFeatureCollection<Geometry, PdsProperties> | null) => void;
  setSelectedPdsShop: (shop: PdsShop | null) => void;
  setActiveDistrict: (district: string | null) => void;
  setJurisdictionDetails: (details: TnebSection | null, geometry?: Geometry | null) => void;
  setIsResolving: (val: boolean) => void;
  setIsLocating: (val: boolean) => void;
  setIsSearching: (val: boolean) => void;
  setIsVeterinaryLoading: (val: boolean) => void;
  setSidebarOpen: (val: boolean) => void;
  setTriggerLocateMe: (val: boolean) => void;
  setReportModal: (isOpen: boolean, context?: { type: string; data: { label: string; value: string }[] } | null) => void;
  setNoDataFound: (val: boolean, point?: { lat: number; lng: number } | null) => void;
  clearSearch: () => void;
  toggleTheme: () => void;
  isUserTyping: boolean;
  setUserTyping: (isTyping: boolean) => void;
  setConstituencyType: (type: 'AC' | 'PC') => void;
  setAcData: (data: GisFeatureCollection<Geometry, ConstituencyProperties> | null) => void;
  setPcData: (data: GisFeatureCollection<Geometry, ConstituencyProperties> | null) => void;
  setPoliceBoundariesData: (data: GisFeatureCollection<Geometry, PoliceBoundaryProperties> | null) => void;
  setPoliceStationsData: (data: GisFeatureCollection<Point, PoliceStationProperties> | null) => void;
  setSelectedPoliceStation: (station: PoliceStationProperties | null, geometry?: Geometry | null) => void;
  setPoliceResolution: (result: PoliceResolutionResult | null) => void;
  setSelectedPostalOffices: (offices: PostalOffice[] | null) => void;
  setSelectedPostalOffice: (office: PostalOffice | null) => void;
  setLegalModal: (open: boolean, tab?: 'about' | 'disclaimer' | 'legal') => void;
  setHealthManifest: (manifest: HealthManifest | null) => void;
  setHealthPriorityData: (data: GisFeatureCollection<Point, HealthFacilityProperties> | null) => void;
  setHealthDistrictData: (data: GisFeatureCollection<Point, HealthFacilityProperties> | null) => void;
  setSelectedHealthFacility: (facility: HealthFacility | null) => void;
  setHealthScope: (scope: HealthScope) => void;
  setHealthFilters: (filters: HealthFilters) => void;
  setTargetHealthScope: (scope: HealthScope | null) => void;
  setHealthSummary: (summary: HealthSummary | null) => void;
  setIsHealthLoading: (loading: boolean) => void;
  setVeterinaryData: (data: GisFeatureCollection<Point, VeterinaryFacilityProperties> | null) => void;
  setSelectedVeterinaryFacility: (facility: VeterinaryFacility | null, geometry?: Geometry | null) => void;
  setVeterinaryFilters: (filters: string[]) => void;
  setRegistrationData: (data: GisFeatureCollection | null) => void;
  setSelectedRegistrationOffice: (office: GisFeature | null, geometry?: Geometry | null) => void;
  setPostalFilters: (filters: Partial<MapState['postalFilters']>) => void;
  setHasNetworkError: (val: boolean) => void;
  setIsResultMinimized: (val: boolean) => void;
  setElectionResults: (results: Record<number, ElectionResult> | null, lastUpdated?: string, summary?: StateSummary[]) => void;
  setIsElectionLive: (live: boolean) => void;
  fetchStateSummary: () => Promise<void>;
  setPendingPincodeResolve: (val: string | null) => void;
  setPendingVeterinaryResolve: (val: string | null) => void;
  setPendingRegistrationResolve: (val: string | null) => void;
  setPendingLandParcelResolve: (val: string | null) => void;
  setAutoLocateAttempted: (val: boolean) => void;
  setLocateTriggerSource: (source: 'auto' | 'manual' | null) => void;
  setSelectedLandParcel: (parcel: LandParcel | null) => void;
  setIsLandParcelLoading: (val: boolean) => void;
  setLandParcelError: (error: 'timeout' | 'error' | null) => void;
  setSelectedTemples: (temples: TempleProperties[] | null) => void;
  setSelectedTemple: (temple: TempleProperties | null) => void;
  setPendingTempleResolve: (val: string | null) => void;
  templeFilters: {
    relics: boolean;
    heritage: boolean;
    activeHubs: boolean;
  };
  setTempleFilters: (filters: Partial<MapState['templeFilters']>) => void;
  setRadioData: (data: GisFeatureCollection<Point, RadioStationProperties> | null) => void;
  setSelectedRadioStation: (station: RadioStation | null) => void;
  setIsRadioPlaying: (playing: boolean) => void;
  fuelRadiusKm: number;
  setFuelRadiusKm: (radius: number) => void;
  setFuelManifest: (manifest: FuelManifest | null) => void;
  setFuelDistrictData: (data: GisFeatureCollection<Point, FuelStationProperties> | null) => void;
  setSelectedFuelStation: (station: FuelStation | null) => void;
  setFuelScope: (scope: FuelScope) => void;
  setFuelFilters: (filters: Partial<FuelFilters>) => void;
  setFuelSummary: (summary: FuelSummary | null) => void;
  setIsFuelLoading: (loading: boolean) => void;
}

export const useMapStore = create<MapState>((set) => ({
  view: {
    center: [11.1271, 78.6569],
    zoom: 7,
  },
  activeLayer: 'PINCODE', // Default to PINCODE
  searchQuery: '',
  searchSuggestions: [],
  selectedSuggestion: null,
  focusedSuggestionIndex: -1,
  searchResult: null,
  districtsData: null,
  stateBoundaryData: null,
  pdsData: null,
  selectedPdsShop: null,
  activeDistrict: null,
  jurisdictionDetails: null,
  jurisdictionGeometry: null,
  isResolving: false,
  isLocating: false,
  isSearching: false,
  isVeterinaryLoading: false,
  theme: 'light',
  isSidebarOpen: true,
  triggerLocateMe: false,
  isUserTyping: false,
  isReportModalOpen: false,
  reportContext: null,
  noDataFound: false,
  selectedLandParcel: null,
  isLandParcelLoading: false,
  landParcelError: null,
  lastClickedPoint: null,
  constituencyType: 'AC',
  acData: null,
  pcData: null,
  policeBoundariesData: null,
  policeStationsData: null,
  selectedPoliceStation: null,
  policeResolution: null,
  selectedPostalOffices: null,
  selectedPostalOffice: null,
  healthManifest: null,
  healthPriorityData: null,
  healthDistrictData: null,
  selectedHealthFacility: null,
  healthScope: 'STATE',
  healthFilters: {
    facilityTypes: ['MCH', 'DH', 'SDH', 'CHC', 'PHC', 'HSC'],
    locationType: 'All',
    isHwc: null,
    hasDelivery: null,
    isFru: null,
    is24x7: null,
    hasBloodBank: null,
    hasBloodStorage: null,
    hasSncu: null,
    hasNbsu: null,
    hasDeic: null,
    hasCt: null,
    hasMri: null,
    hasDialysis: null,
    hasCbnaat: null,
    hasTeleConsultation: null,
    hasStemiHub: null,
    hasStemiSpoke: null,
    hasCathLab: null
  },
  healthSummary: null,
  isHealthLoading: false,
  targetHealthScope: null,
  veterinaryData: null,
  selectedVeterinaryFacility: null,
  veterinaryFilters: ['POLY_CLINIC', 'CLINIC_CENTER', 'HOSPITAL', 'DISPENSARY', 'SUB_CENTER'],
  registrationData: null,
  selectedRegistrationOffice: null,
  isLegalModalOpen: false,
  legalTab: 'about',
  selectedLocalBodyV2: null,
  isV2Loading: false,
  language: 'en',
  globalLocation: null,
  userLocation: null,
  postalFilters: {
    delivery: 'All',
    type: 'All'
  },
  hasSeenTutorial: localStorage.getItem('nm_has_seen_tutorial') === 'true',
  isTutorialOpen: false,
  hasNetworkError: false,
  isResultMinimized: false,
  election: {
    results: null,
    lastUpdated: null,
    isLive: false,
    stateSummary: null,
    isLoading: false
  },
  pendingPincodeResolve: null,
  pendingVeterinaryResolve: null,
  pendingRegistrationResolve: null,
  pendingLandParcelResolve: null,
  autoLocateAttempted: false,
  locateTriggerSource: null,
  selectedTemples: null,
  selectedTemple: null,
  pendingTempleResolve: null,
  templeFilters: {
    relics: false,
    heritage: false,
    activeHubs: false
  },
  radioData: null,
  selectedRadioStation: null,
  isRadioPlaying: false,
  fuelManifest: null,
  fuelRadiusKm: 5.0,
  fuelDistrictData: null,
  selectedFuelStation: null,
  fuelScope: 'PINCODE',
  fuelFilters: {
    brands: [],
    fuelGroups: []
  },
  fuelSummary: null,
  isFuelLoading: false,

  setView: (center, zoom) => set({ view: { center, zoom } }),
  setHasSeenTutorial: (val) => {
    localStorage.setItem('nm_has_seen_tutorial', val.toString());
    set({ hasSeenTutorial: val });
  },
  setIsTutorialOpen: (val) => set({ isTutorialOpen: val }),
  setActiveLayer: (layer) => set((state) => {
    trackEvent('layer_switch', { layer });
    return { 
      activeLayer: layer,
      jurisdictionDetails: null,
      jurisdictionGeometry: null,
      selectedPdsShop: null,
      selectedPoliceStation: null,
      policeResolution: null,
      selectedPostalOffices: null,
      selectedPostalOffice: null,
      selectedHealthFacility: null,
      selectedLocalBodyV2: null,
      selectedVeterinaryFacility: null,
      selectedRegistrationOffice: null,
      selectedLandParcel: null,
      landParcelError: null,
      selectedTemples: null,
      selectedTemple: null,
      healthDistrictData: state.healthDistrictData,
      healthScope: state.healthScope,
      healthSummary: state.healthSummary,
      activeDistrict: state.activeDistrict,
      healthFilters: state.healthFilters,
      pendingPincodeResolve: state.activeLayer === 'PINCODE' ? state.pendingPincodeResolve : null,
      pendingVeterinaryResolve: state.activeLayer === 'VETERINARY' ? state.pendingVeterinaryResolve : null,
      pendingRegistrationResolve: state.activeLayer === 'REGISTRATION' ? state.pendingRegistrationResolve : null,
      pendingTempleResolve: state.activeLayer === 'TEMPLES' ? state.pendingTempleResolve : null,
      selectedRadioStation: null,
      isRadioPlaying: false,
      selectedFuelStation: null,
      fuelDistrictData: state.activeLayer === 'FUEL' ? state.fuelDistrictData : null,
      fuelSummary: state.activeLayer === 'FUEL' ? state.fuelSummary : null,
      // Clear search when navigating to RADIO module
      searchQuery: layer === 'RADIO' ? '' : state.searchQuery,
      searchSuggestions: layer === 'RADIO' ? [] : state.searchSuggestions,
      noDataFound: layer === 'RADIO' ? false : state.noDataFound,
      // Reset result if switching from/to layers
      searchResult: null,
      isResultMinimized: false
    };
  }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSearchSuggestions: (suggestions) => set({ searchSuggestions: suggestions }),
  setSelectedSuggestion: (suggestion) => set({ selectedSuggestion: suggestion, focusedSuggestionIndex: -1 }),
  setFocusedSuggestionIndex: (index) => set({ focusedSuggestionIndex: index }),
  setSearchResult: (result, keepSelection = false, updateQuery = false) => set((state) => {
    const newState: Partial<MapState> = { 
      searchResult: result,
      jurisdictionDetails: keepSelection ? state.jurisdictionDetails : null,
      jurisdictionGeometry: keepSelection ? state.jurisdictionGeometry : null,
      selectedPdsShop: keepSelection ? state.selectedPdsShop : null,
      selectedPoliceStation: keepSelection ? state.selectedPoliceStation : null,
      policeResolution: keepSelection ? state.policeResolution : null,
      selectedPostalOffices: keepSelection ? state.selectedPostalOffices : null,
      selectedPostalOffice: keepSelection ? state.selectedPostalOffice : null,
      selectedHealthFacility: keepSelection ? state.selectedHealthFacility : null,
      selectedVeterinaryFacility: keepSelection ? state.selectedVeterinaryFacility : null,
      selectedRegistrationOffice: keepSelection ? state.selectedRegistrationOffice : null,
      selectedLandParcel: keepSelection ? state.selectedLandParcel : null,
      selectedTemples: keepSelection ? state.selectedTemples : null,
      selectedTemple: keepSelection ? state.selectedTemple : null,
      selectedRadioStation: keepSelection ? state.selectedRadioStation : null,
      healthSummary: keepSelection ? state.healthSummary : null,
      isResultMinimized: false,
      noDataFound: false
    };

    if (updateQuery && result) {
      const p = result.properties;
      const num = (p.assembly_1 || p.parliament || p.ps_code || '').toString();
      const baseName = (p.facility_n || p.village_na || p.ps_name || p.assembly_c || p.parliame_1 || p.office_name || p.district || p.NAME || '').toString();
      const typePrefix = p.ps_name ? 'Station' : p.assembly_c ? 'AC' : p.parliame_1 ? 'PC' : '';
      const name = num ? `${typePrefix} #${num} - ${baseName}` : baseName;
      const pin = (p.PIN_CODE || p.pincode)?.toString();
      newState.searchQuery = pin ? `${pin} - ${name}` : name;
      newState.isUserTyping = false;
    }

    return newState;
  }),
  setDistrictsData: (data) => set({ districtsData: data }),
  setStateBoundaryData: (data) => set({ stateBoundaryData: data }),
  setPdsData: (data: GisFeatureCollection<Geometry, PdsProperties> | null) => set({ pdsData: data }),
  setSelectedPdsShop: (shop) => set({ selectedPdsShop: shop, isResultMinimized: false }),
  setActiveDistrict: (district) => set({ activeDistrict: district }),
  setJurisdictionDetails: (details, geometry = null) => set({ 
    jurisdictionDetails: details, 
    jurisdictionGeometry: geometry,
    isResultMinimized: false
  }),
  setIsResolving: (val) => set({ isResolving: val }),
  setIsLocating: (val) => set({ isLocating: val }),
  setIsSearching: (val) => set({ isSearching: val }),
  setIsVeterinaryLoading: (val) => set({ isVeterinaryLoading: val }),
  setSidebarOpen: (val) => set({ isSidebarOpen: val }),
  setTriggerLocateMe: (trigger) => set({ triggerLocateMe: trigger, isUserTyping: false }),
  setUserTyping: (isTyping) => set({ isUserTyping: isTyping }),
  setReportModal: (isOpen, context = null) => set({ 
    isReportModalOpen: isOpen, 
    reportContext: context 
  }),
  setNoDataFound: (val, point = null) => set({ 
    noDataFound: val, 
    lastClickedPoint: point,
    // Clear other data to show only the "No Data" card
    searchResult: val ? null : undefined,
    jurisdictionDetails: val ? null : undefined,
    selectedPoliceStation: val ? null : undefined,
    policeResolution: val ? null : undefined,
    selectedPostalOffices: val ? null : undefined,
    selectedPostalOffice: val ? null : undefined,
    selectedHealthFacility: val ? null : undefined,
    selectedVeterinaryFacility: val ? null : undefined,
    selectedRegistrationOffice: val ? null : undefined,
    selectedTemples: val ? null : undefined,
    selectedTemple: val ? null : undefined,
    selectedRadioStation: val ? null : undefined,
    isResultMinimized: false
  }),
  clearSearch: () => set((state) => ({ 
    searchQuery: '', 
    searchSuggestions: [],
    selectedSuggestion: null,
    focusedSuggestionIndex: -1,
    searchResult: null, 
    pdsData: null, 
    selectedPdsShop: null,
    activeDistrict: state.activeLayer === 'HEALTH' ? state.activeDistrict : null,
    jurisdictionDetails: null,
    jurisdictionGeometry: null,
    selectedPoliceStation: null,
    policeResolution: null,
    selectedPostalOffices: null,
    selectedPostalOffice: null,
    selectedHealthFacility: null,
    selectedLocalBodyV2: null,
    selectedVeterinaryFacility: null,
    selectedRegistrationOffice: null,
    selectedLandParcel: null,
    landParcelError: null,
    selectedTemples: null,
    selectedTemple: null,
    healthDistrictData: state.activeLayer === 'HEALTH' ? state.healthDistrictData : null,
    fuelDistrictData: state.activeLayer === 'FUEL' ? state.fuelDistrictData : null,
    pendingPincodeResolve: null,
    pendingVeterinaryResolve: null,
    pendingRegistrationResolve: null,
    pendingTempleResolve: null,
    templeFilters: {
      relics: false,
      heritage: false,
      activeHubs: false
    },
    selectedRadioStation: null,
    selectedFuelStation: null,
    noDataFound: false,
    lastClickedPoint: null,
    globalLocation: null,
    userLocation: null,
    isResultMinimized: false
  })),
  toggleTheme: () => set((state) => {
    const newTheme = state.theme === 'dark' ? 'light' : 'dark';
    trackEvent('theme_toggle', { theme: newTheme });
    return { theme: newTheme };
  }),
  setConstituencyType: (type) => set({ constituencyType: type, searchResult: null }),
  setAcData: (data) => set({ acData: data }),
  setPcData: (data) => set({ pcData: data }),
  setPoliceBoundariesData: (data) => set({ policeBoundariesData: data }),
  setPoliceStationsData: (data) => set({ policeStationsData: data }),
  setSelectedPoliceStation: (station, geometry = null) => set({ 
    selectedPoliceStation: station ? { 
      type: 'Feature', 
      properties: station as PoliceStationProperties, 
      geometry: { 
        type: 'Point', 
        coordinates: (station as PoliceStationProperties).station_location || [78.6569, 11.1271] 
      } as Point 
    } : null,
    jurisdictionGeometry: geometry || null,
    isResultMinimized: false
  }),
  setPoliceResolution: (result) => set({ 
    policeResolution: result,
    selectedPoliceStation: result?.station || null,
    jurisdictionGeometry: result?.boundary.geometry || null,
    isResultMinimized: false
  }),
  setSelectedPostalOffices: (offices) => set({ selectedPostalOffices: offices }),
  setSelectedPostalOffice: (office) => set({ selectedPostalOffice: office, isResultMinimized: false }),
  setLegalModal: (open, tab) => set((state) => ({ 
    isLegalModalOpen: open, 
    legalTab: tab || state.legalTab 
  })),
  setHealthManifest: (manifest) => set({ healthManifest: manifest }),
  setHealthPriorityData: (data) => set({ healthPriorityData: data }),
  setHealthDistrictData: (data) => set({ healthDistrictData: data }),
  setIsHealthLoading: (loading: boolean) => set({ isHealthLoading: loading }),
  setSelectedHealthFacility: (facility) => set({ selectedHealthFacility: facility, isResultMinimized: false }),
  setHealthScope: (scope) => set({ healthScope: scope }),
  setHealthFilters: (filters) => set({ healthFilters: filters }),
  setTargetHealthScope: (scope) => set({ targetHealthScope: scope }),
  setHealthSummary: (summary) => set({ healthSummary: summary }),
  setVeterinaryData: (data) => set({ veterinaryData: data }),
  setSelectedVeterinaryFacility: (facility, geometry = null) => set({ 
    selectedVeterinaryFacility: facility, 
    jurisdictionGeometry: geometry || null, 
    isResultMinimized: false 
  }),
  setVeterinaryFilters: (filters) => set({ veterinaryFilters: filters }),
  setRegistrationData: (data) => set({ registrationData: data }),
  setSelectedRegistrationOffice: (office, geometry = null) => set({ 
    selectedRegistrationOffice: office,
    jurisdictionGeometry: geometry || null,
    isResultMinimized: false
  }),
  setPostalFilters: (filters) => set((state) => ({
    postalFilters: { ...state.postalFilters, ...filters }
  })),
  setSelectedLocalBodyV2: (feature) => set({ selectedLocalBodyV2: feature, isResolving: false, isV2Loading: false, isResultMinimized: false }),
  setIsV2Loading: (val) => set({ isV2Loading: val }),
  setLanguage: (lang) => {
    trackEvent('language_switch', { language: lang });
    set({ language: lang });
  },
  setGlobalLocation: (location) => set({ globalLocation: location }),
  setUserLocation: (location) => set({ userLocation: location }),
  setHasNetworkError: (val) => set({ hasNetworkError: val }),
  setIsResultMinimized: (val) => set({ isResultMinimized: val }),
  setElectionResults: (results, lastUpdated, summary) => set((state) => ({ 
    election: {
      ...state.election,
      results, 
      stateSummary: summary || state.election.stateSummary,
      lastUpdated: lastUpdated || new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
    }
  })),
  setIsElectionLive: (live) => set((state) => ({ 
    election: { ...state.election, isLive: live } 
  })),
  fetchStateSummary: async () => {
    // Handled statically now during initial results load, keep skeleton for backward compatibility
    return Promise.resolve();
  },
  setPendingPincodeResolve: (val) => set({ pendingPincodeResolve: val }),
  setPendingVeterinaryResolve: (val) => set({ pendingVeterinaryResolve: val }),
  setPendingRegistrationResolve: (val) => set({ pendingRegistrationResolve: val }),
  setPendingLandParcelResolve: (val) => set({ pendingLandParcelResolve: val }),
  setAutoLocateAttempted: (val) => set({ autoLocateAttempted: val }),
  setLocateTriggerSource: (source) => set({ locateTriggerSource: source }),
  setSelectedLandParcel: (parcel) => set({ selectedLandParcel: parcel }),
  setIsLandParcelLoading: (val) => set({ isLandParcelLoading: val }),
  setLandParcelError: (error) => set({ landParcelError: error }),
  setSelectedTemples: (temples) => set({ selectedTemples: temples }),
  setSelectedTemple: (temple) => set({ selectedTemple: temple, isResultMinimized: false }),
  setPendingTempleResolve: (val) => set({ pendingTempleResolve: val }),
  setTempleFilters: (filters) => set((state) => ({
    templeFilters: { ...state.templeFilters, ...filters }
  })),
  setRadioData: (data) => set({ radioData: data }),
  setSelectedRadioStation: (station) => set({ selectedRadioStation: station, isResultMinimized: false }),
  setIsRadioPlaying: (playing) => set({ isRadioPlaying: playing }),
  setFuelRadiusKm: (radius) => set({ fuelRadiusKm: radius }),
  setFuelManifest: (manifest) => set({ fuelManifest: manifest }),
  setFuelDistrictData: (data) => set({ fuelDistrictData: data }),
  setSelectedFuelStation: (station) => set({ selectedFuelStation: station, isResultMinimized: false }),
  setFuelScope: (scope) => set({ fuelScope: scope }),
  setFuelFilters: (filters) => set((state) => ({
    fuelFilters: { ...state.fuelFilters, ...filters }
  })),
  setFuelSummary: (summary) => set({ fuelSummary: summary }),
  setIsFuelLoading: (loading) => set({ isFuelLoading: loading }),
}));
